import React, { useState } from 'react';
import { 
  Box, 
  Container, 
  Paper, 
  TextField, 
  Button, 
  Typography, 
  Tabs, 
  Tab, 
  Alert,
  InputAdornment,
  IconButton,
  CircularProgress,
  Checkbox,
  Link
} from '@mui/material';
import { 
  Visibility, 
  VisibilityOff, 
  Email, 
  Lock, 
  Person, 
  FamilyRestroom,
  EmojiEvents,
  Google,
  ChildCare,
  VpnKey
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { sendPasswordResetEmail } from 'firebase/auth';
import { auth } from '../firebase';
import { 
  findFamilyByCode, 
  getChildrenForFamily, 
  registerChild, 
  authenticateChild,
  validateFamilyCode,
  validatePIN
} from '../utils/childAuthHelper';

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`auth-tabpanel-${index}`}
      aria-labelledby={`auth-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function AuthPage() {
  const [tabValue, setTabValue] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    displayName: '',
    familyCode: '', // For joining families
    role: 'parent', // Default to parent
    // Child-specific fields
    childFamilyCode: '',
    childFirstName: '',
    childPIN: '',
    childConfirmPIN: '',
    childDateOfBirth: ''
  });
  const [ageConfirmed, setAgeConfirmed] = useState(false);
  
  // Child authentication states
  const [childAuthStep, setChildAuthStep] = useState('code'); // 'code', 'select', 'register', 'pin'
  const [availableChildren, setAvailableChildren] = useState([]);
  const [selectedChild, setSelectedChild] = useState(null);
  const [verifiedFamilyId, setVerifiedFamilyId] = useState(null);

  const { signup, login, loginChild, signInWithGoogle, fetchUserProfile } = useAuth();
  const navigate = useNavigate();

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    setError('');
    setSuccess('');
    setFormData({
      email: '',
      password: '',
      confirmPassword: '',
      displayName: '',
      familyCode: '',
      role: 'parent',
      childFamilyCode: '',
      childFirstName: '',
      childPIN: '',
      childConfirmPIN: '',
      childDateOfBirth: ''
    });
    // Reset child auth state
    setChildAuthStep('code');
    setAvailableChildren([]);
    setSelectedChild(null);
    setVerifiedFamilyId(null);
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      if (tabValue === 0) {
        // Sign up
        
        // COPPA Compliance: Prevent direct child account creation
        if (formData.role === 'child') {
          setError('Children cannot create accounts directly. Please ask your parent to add you to their family account.');
          setLoading(false);
          return;
        }
        
        // Parent signup validation
        if (formData.password !== formData.confirmPassword) {
          throw new Error('Passwords do not match');
        }
        if (formData.password.length < 6) {
          throw new Error('Password must be at least 6 characters');
        }
        await signup(formData.email, formData.password, formData.displayName, formData.role);
        // Redirect to family setup for new users
        setTimeout(() => {
          navigate('/family-setup');
        }, 1000);
      } else {
        // Sign in
        const result = await login(formData.email, formData.password);
        // Fetch the user profile to get the role
        const profile = await fetchUserProfile(result.user.uid);
        // Navigate based on role
        if (profile?.role === 'parent') {
          navigate('/parent');
        } else if (profile?.role === 'child') {
          navigate('/child');
        } else {
          // Fallback
          navigate('/child');
        }
      }
    } catch (error) {
      setError(error.message);
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!formData.email) {
      setError('Please enter your email address first');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await sendPasswordResetEmail(auth, formData.email);
      setSuccess('Password reset email sent! Check your inbox.');
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const { user, isNewUser } = await signInWithGoogle(formData.role);
      
      if (isNewUser) {
        // New user - go to family setup
        navigate('/family-setup');
      } else {
        // Existing user - fetch profile and navigate
        const profile = await fetchUserProfile(user.uid);
        if (profile?.role === 'parent') {
          navigate('/parent');
        } else {
          navigate('/child');
        }
      }
    } catch (error) {
      console.error('Google Sign-In Error:', error);
      if (error.code === 'auth/popup-closed-by-user') {
        setError('Sign-in cancelled');
      } else if (error.code === 'auth/popup-blocked') {
        setError('Popup blocked. Please allow popups for this site.');
      } else {
        setError(error.message || 'Failed to sign in with Google');
      }
      setLoading(false);
    }
  };

  // Child Authentication Handlers

  const handleVerifyFamilyCode = async () => {
    if (!formData.childFamilyCode || !validateFamilyCode(formData.childFamilyCode)) {
      setError('Please enter a valid 8-character family code');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const family = await findFamilyByCode(formData.childFamilyCode);
      setVerifiedFamilyId(family.familyId);
      
      // Get existing children for this family
      const children = await getChildrenForFamily(family.familyId);
      setAvailableChildren(children);
      
      if (children.length > 0) {
        setChildAuthStep('select');
        setSuccess(`Found family: ${family.name}`);
      } else {
        setChildAuthStep('register');
        setSuccess(`Welcome to ${family.name}! Let's set up your profile.`);
      }
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectExistingChild = (child) => {
    setSelectedChild(child);
    setChildAuthStep('pin');
    setError('');
  };

  const handleRegisterNewChild = () => {
    setChildAuthStep('register');
    setError('');
  };

  const handleChildRegister = async () => {
    if (!formData.childFirstName || formData.childFirstName.trim().length < 2) {
      setError('Please enter your first name (at least 2 characters)');
      return;
    }

    if (!validatePIN(formData.childPIN)) {
      setError('PIN must be exactly 4 digits');
      return;
    }

    if (formData.childPIN !== formData.childConfirmPIN) {
      setError('PINs do not match');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const childData = await registerChild(
        verifiedFamilyId,
        formData.childFirstName,
        formData.childPIN,
        formData.childDateOfBirth || null
      );

      // Use loginChild to update AuthContext state AND persist session —
      // calling setChildSession alone won't update React state after mount.
      await loginChild(childData);
      setSuccess(`Welcome, ${formData.childFirstName}! 🎉`);
      
      setTimeout(() => {
        navigate('/child');
      }, 1000);
    } catch (error) {
      setError(error.message);
      setLoading(false);
    }
  };

  const handleChildLogin = async () => {
    if (!validatePIN(formData.childPIN)) {
      setError('PIN must be 4 digits');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const childData = await authenticateChild(
        verifiedFamilyId,
        selectedChild.id,
        formData.childPIN
      );

      // loginChild updates AuthContext state directly AND persists to localStorage.
      // This is critical — setChildSession alone won't update React context state.
      await loginChild(childData);
      setSuccess(`Welcome back, ${selectedChild.firstName}! 🎉`);
      
      setTimeout(() => {
        navigate('/child');
      }, 1000);
    } catch (error) {
      setError(error.message);
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #4CAF50 0%, #81C784 50%, #FFB74D 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 2
      }}
    >
      <Container maxWidth="sm">
        <Paper
          elevation={10}
          sx={{
            borderRadius: 3,
            overflow: 'hidden',
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)'
          }}
        >
          {/* Header */}
          <Box
            sx={{
              background: 'linear-gradient(45deg, #6A1B9A, #8E24AA, #AB47BC)',
              color: 'white',
              textAlign: 'center',
              py: 4,
              px: 3
            }}
          >
            <Box sx={{ fontSize: 60, mb: 2, filter: 'drop-shadow(3px 3px 6px rgba(0,0,0,0.4))' }}>
              🤖
            </Box>
            <Typography 
              variant="h3" 
              component="h1" 
              fontWeight="bold"
              sx={{
                textShadow: '3px 3px 8px rgba(0,0,0,0.5)',
                letterSpacing: '2px',
                fontFamily: "'Poppins', sans-serif"
              }}
            >
              Earn-E Chore Helper
            </Typography>
            <Typography 
              variant="h6" 
              sx={{ 
                mt: 1,
                textShadow: '2px 2px 4px rgba(0,0,0,0.4)',
                fontWeight: 500,
                fontFamily: "'Inter', sans-serif"
              }}
            >
              Earn, Learn, and Grow Together! 🌟
            </Typography>
          </Box>

          {/* Tabs */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs 
              value={tabValue} 
              onChange={handleTabChange} 
              centered
              variant="fullWidth"
              sx={{
                '& .MuiTab-root': {
                  fontSize: {xs: '0.85rem', sm: '1rem'},
                  fontWeight: 600,
                  py: 2,
                  minHeight: 64,
                  // Ensure 44x44px touch target for mobile accessibility
                  minWidth: '44px',
                  padding: '12px 16px'
                }
              }}
            >
              <Tab 
                label="Parent Sign Up" 
                icon={<FamilyRestroom />} 
                iconPosition="start"
                sx={{ color: '#4CAF50' }}
              />
              <Tab 
                label="Parent Sign In" 
                icon={<EmojiEvents />} 
                iconPosition="start"
                sx={{ color: '#4CAF50' }}
              />
              <Tab 
                label="Child Access" 
                icon={<ChildCare />} 
                iconPosition="start"
                sx={{ color: '#FF9800' }}
              />
            </Tabs>
          </Box>

          {/* Error Alert */}
          {error && (
            <Box sx={{ p: 2 }}>
              <Alert severity="error" sx={{ borderRadius: 2 }}>
                {error}
              </Alert>
            </Box>
          )}

          {/* Success Alert */}
          {success && (
            <Box sx={{ p: 2 }}>
              <Alert severity="success" sx={{ borderRadius: 2 }}>
                {success}
              </Alert>
            </Box>
          )}

          {/* Sign Up Form */}
          <TabPanel value={tabValue} index={0}>
            <form onSubmit={handleSubmit}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {/* COPPA Compliance: Removed child signup option */}
                {/* Children must be added by parents through the family dashboard */}
                <Alert severity="info" sx={{ borderRadius: 2 }}>
                  <Typography variant="body2">
                    <strong>Parents:</strong> Create your family account here. You can add children from your dashboard after signing up.
                  </Typography>
                </Alert>

                <TextField
                  name="displayName"
                  label="Full Name"
                  autoComplete="name"
                  value={formData.displayName}
                  onChange={handleInputChange}
                  required
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Person color="primary" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2
                    }
                  }}
                />

                {/* COPPA Compliance: Email required for parent signup */}
                <TextField
                  name="email"
                  label="Email Address"
                  type="email"
                  autoComplete="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Email color="primary" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2
                    }
                  }}
                />

                {/* COPPA Compliance: Password fields for parent signup */}
                <TextField
                  name="password"
                  label="Password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  value={formData.password}
                  onChange={handleInputChange}
                  required
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Lock color="primary" />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                          aria-label="toggle password visibility"
                          sx={{ 
                            minWidth: 48, 
                            minHeight: 48,
                            padding: '12px',
                            '& .MuiSvgIcon-root': {
                              fontSize: '24px'
                            }
                          }}
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2
                    }
                  }}
                  helperText={formData.password.length > 0 && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="caption" color={formData.password.length < 6 ? 'error' : 'success'}>
                        {formData.password.length < 6 ? 'Too short' : '✓ Minimum length'}
                      </Typography>
                      <Typography variant="caption" color={!/[A-Z]/.test(formData.password) ? 'text.secondary' : 'success'}>
                        {!/[A-Z]/.test(formData.password) ? 'No uppercase' : '✓ Uppercase'}
                      </Typography>
                      <Typography variant="caption" color={!/[0-9]/.test(formData.password) ? 'text.secondary' : 'success'}>
                        {!/[0-9]/.test(formData.password) ? 'No number' : '✓ Number'}
                      </Typography>
                    </Box>
                  )}
                />

                <TextField
                  name="confirmPassword"
                  label="Confirm Password"
                  type={showConfirmPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  required
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Lock color="primary" />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle confirm password visibility"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          edge="end"
                          sx={{ 
                            minWidth: 48, 
                            minHeight: 48,
                            padding: '12px',
                            '& .MuiSvgIcon-root': {
                              fontSize: '24px'
                            }
                          }}
                        >
                          {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2
                    }
                  }}
                />

                {/* Age Confirmation - COPPA Compliance - C3: Fully clickable label */}
                <Box
                  component="label"
                  htmlFor="age-confirmation-checkbox"
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    minHeight: '48px',
                    minWidth: '48px',
                    mb: 2,
                    padding: '8px 0',
                    cursor: 'pointer',
                    '&:hover': {
                      bgcolor: 'rgba(0, 0, 0, 0.02)',
                      borderRadius: 1
                    }
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      minWidth: '48px',
                      minHeight: '48px',
                      mr: 1
                    }}
                  >
                    <Checkbox
                      id="age-confirmation-checkbox"
                      checked={ageConfirmed}
                      onChange={(e) => {
                        e.stopPropagation();
                        setAgeConfirmed(e.target.checked);
                      }}
                      onClick={(e) => e.stopPropagation()}
                      color="primary"
                      sx={{ 
                        width: 48, 
                        height: 48,
                        padding: '12px',
                        '& .MuiSvgIcon-root': {
                          width: 28,
                          height: 28,
                        }
                      }}
                    />
                  </Box>
                  <Box sx={{ flex: 1, pointerEvents: 'none' }}>
                    <Typography variant="body1" sx={{ fontSize: '14px !important', lineHeight: 1.5, pointerEvents: 'auto' }}>
                      I confirm that I am at least 18 years old and all children I add to this account will be at least 13 years old. I have read and agree to the Terms of Service and Privacy Policy.
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
                      <Link
                        href="/terms-of-service"
                        target="_blank"
                        underline="hover"
                        onClick={(e) => e.stopPropagation()}
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          minHeight: '48px',
                          minWidth: '48px',
                          px: 2,
                          py: 1.5,
                          borderRadius: 1,
                          border: '1px solid',
                          borderColor: 'primary.main',
                          fontSize: '14px',
                          fontWeight: 500,
                          textDecoration: 'none',
                          '&:hover': {
                            bgcolor: 'rgba(25, 118, 210, 0.04)',
                            textDecoration: 'none'
                          }
                        }}
                      >
                        Terms of Service ↗
                      </Link>
                      <Link
                        href="/privacy-policy"
                        target="_blank"
                        underline="hover"
                        onClick={(e) => e.stopPropagation()}
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          minHeight: '48px',
                          minWidth: '48px',
                          px: 2,
                          py: 1.5,
                          borderRadius: 1,
                          border: '1px solid',
                          borderColor: 'primary.main',
                          fontSize: '14px',
                          fontWeight: 500,
                          textDecoration: 'none',
                          '&:hover': {
                            bgcolor: 'rgba(25, 118, 210, 0.04)',
                            textDecoration: 'none'
                          }
                        }}
                      >
                        Privacy Policy ↗
                      </Link>
                    </Box>
                  </Box>
                </Box>

                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={loading || !ageConfirmed}
                  sx={{
                    py: 1.5,
                    px: 2,
                    minHeight: 48,
                    fontSize: { xs: '0.95rem', sm: '1.1rem' },
                    fontWeight: 600,
                    borderRadius: 2,
                    whiteSpace: 'normal',
                    textAlign: 'center',
                    lineHeight: 1.4,
                    background: 'linear-gradient(45deg, #4CAF50, #81C784)',
                    '&:hover': {
                      background: 'linear-gradient(45deg, #388E3C, #4CAF50)',
                    },
                    '&:disabled': {
                      opacity: 0.5,
                      cursor: 'not-allowed'
                    }
                  }}
                >
                  {loading ? 'Creating Account...' : 'Join the Family!'}
                </Button>
                {(!ageConfirmed || loading) && (
                  <Typography variant="body2" sx={{ fontSize: '14px', color: '#666', mt: 1 }}>
                    Fill all fields and agree to terms to continue
                  </Typography>
                )}

                {/* Divider */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, my: 1 }}>
                  <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
                  <Typography variant="body2" color="text.secondary">
                    OR
                  </Typography>
                  <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
                </Box>

                {/* Google Sign-In Button - COPPA: Must also require age confirmation */}
                <Button
                  onClick={handleGoogleSignIn}
                  variant="outlined"
                  size="large"
                  disabled={loading || !ageConfirmed}
                  startIcon={<Google />}
                  sx={{
                    py: 1.5,
                    px: 3,
                    fontSize: { xs: '0.95rem', sm: '1.1rem' },
                    fontWeight: 600,
                    borderRadius: 2,
                    borderColor: '#4285F4',
                    color: '#4285F4',
                    textTransform: 'none',
                    whiteSpace: 'normal',
                    textAlign: 'center',
                    lineHeight: 1.4,
                    '&:hover': {
                      borderColor: '#357ae8',
                      bgcolor: 'rgba(66, 133, 244, 0.04)',
                    }
                  }}
                >
                  {loading ? 'Signing in...' : 'Continue with Google'}
                </Button>
              </Box>
            </form>
          </TabPanel>

          {/* Sign In Form */}
          <TabPanel value={tabValue} index={1}>
            <form onSubmit={handleSubmit}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <TextField
                  name="email"
                  label="Email Address"
                  type="email"
                  autoComplete="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Email color="primary" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2
                    }
                  }}
                />

                <TextField
                  name="password"
                  label="Password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  value={formData.password}
                  onChange={handleInputChange}
                  required
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Lock color="primary" />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                          aria-label="toggle password visibility"
                          sx={{ minWidth: 48, minHeight: 48 }}
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2
                    }
                  }}
                />

                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={loading}
                  sx={{
                    py: 1.5,
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    borderRadius: 2,
                    background: 'linear-gradient(45deg, #4CAF50, #81C784)',
                    '&:hover': {
                      background: 'linear-gradient(45deg, #388E3C, #4CAF50)',
                    }
                  }}
                >
                  {loading ? 'Signing In...' : 'Welcome Back!'}
                </Button>

                {/* Divider */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, my: 1 }}>
                  <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
                  <Typography variant="body2" color="text.secondary">
                    OR
                  </Typography>
                  <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
                </Box>

                {/* Google Sign-In Button */}
                <Button
                  onClick={handleGoogleSignIn}
                  variant="outlined"
                  size="large"
                  disabled={loading}
                  startIcon={<Google />}
                  sx={{
                    py: 1.5,
                    px: 3,
                    fontSize: { xs: '0.95rem', sm: '1.1rem' },
                    fontWeight: 600,
                    borderRadius: 2,
                    borderColor: '#4285F4',
                    color: '#4285F4',
                    textTransform: 'none',
                    whiteSpace: 'normal',
                    textAlign: 'center',
                    lineHeight: 1.4,
                    '&:hover': {
                      borderColor: '#357ae8',
                      bgcolor: 'rgba(66, 133, 244, 0.04)',
                    }
                  }}
                >
                  {loading ? 'Signing in...' : 'Continue with Google'}
                </Button>

                <Button
                  onClick={handleForgotPassword}
                  disabled={loading}
                  sx={{
                    textTransform: 'none',
                    color: 'text.secondary'
                  }}
                >
                  Forgot Password?
                </Button>
              </Box>
            </form>
          </TabPanel>

          {/* Child Access Tab */}
          <TabPanel value={tabValue} index={2}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Info Alert */}
              <Alert severity="info" sx={{ borderRadius: 2 }}>
                <Typography variant="body2">
                  <strong>Children:</strong> Get your 8-character family code from your parent, then access the app safely without needing an email!
                </Typography>
              </Alert>

              {/* Step 1: Enter Family Code */}
              {childAuthStep === 'code' && (
                <>
                  <TextField
                    name="childFamilyCode"
                    label="Family Code"
                    value={formData.childFamilyCode}
                    onChange={(e) => setFormData({...formData, childFamilyCode: e.target.value.toUpperCase()})}
                    required
                    fullWidth
                    placeholder="ABC123XY"
                    helperText="Enter the 8-character code from your parent"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <VpnKey color="secondary" />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        fontSize: '1.2rem',
                        letterSpacing: '0.2em',
                        fontFamily: 'monospace',
                        textAlign: 'center',
                        justifyContent: 'center'
                      },
                      '& .MuiInputLabel-root': {
                        textAlign: 'center',
                        width: '100%',
                        left: 0
                      },
                      '& .MuiFormHelperText-root': {
                        textAlign: 'center'
                      }
                    }}
                  />

                  <Button
                    onClick={handleVerifyFamilyCode}
                    variant="contained"
                    size="large"
                    disabled={loading || !formData.childFamilyCode}
                    sx={{
                      py: 1.5,
                      fontSize: '1.1rem',
                      fontWeight: 600,
                      borderRadius: 2,
                      background: 'linear-gradient(45deg, #FF9800, #FFB74D)',
                      '&:hover': {
                        background: 'linear-gradient(45deg, #F57C00, #FF9800)',
                      }
                    }}
                  >
                    {loading ? <CircularProgress size={24} color="inherit" /> : 'Continue'}
                  </Button>
                </>
              )}

              {/* Step 2: Select Existing Child or Register New */}
              {childAuthStep === 'select' && (
                <>
                  <Typography variant="h6" gutterBottom>
                    Who are you?
                  </Typography>
                  
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {availableChildren.map((child) => (
                      <Button
                        key={child.id}
                        onClick={() => handleSelectExistingChild(child)}
                        variant="outlined"
                        size="large"
                        sx={{
                          py: 2,
                          justifyContent: 'flex-start',
                          borderRadius: 2,
                          borderWidth: 2,
                          '&:hover': {
                            borderWidth: 2,
                            bgcolor: 'rgba(255, 152, 0, 0.1)'
                          }
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Box
                            component="img"
                            src={child.avatar}
                            alt={child.firstName}
                            sx={{ width: 48, height: 48, borderRadius: '50%' }}
                          />
                          <Typography variant="h6">{child.firstName}</Typography>
                        </Box>
                      </Button>
                    ))}

                    <Button
                      onClick={handleRegisterNewChild}
                      variant="contained"
                      color="secondary"
                      size="large"
                      sx={{
                        py: 2,
                        borderRadius: 2
                      }}
                    >
                      + I'm New Here
                    </Button>
                  </Box>

                  <Button
                    onClick={() => setChildAuthStep('code')}
                    sx={{ textTransform: 'none' }}
                  >
                    ← Back to Family Code
                  </Button>
                </>
              )}

              {/* Step 3: Register New Child */}
              {childAuthStep === 'register' && (
                <>
                  <Typography variant="h6" gutterBottom>
                    Create Your Profile
                  </Typography>

                  <TextField
                    name="childFirstName"
                    label="Your First Name"
                    value={formData.childFirstName}
                    onChange={handleInputChange}
                    required
                    fullWidth
                    placeholder="Emma"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Person color="secondary" />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2
                      }
                    }}
                  />

                  <TextField
                    label="Date of Birth"
                    type="date"
                    name="childDateOfBirth"
                    value={formData.childDateOfBirth || ''}
                    onChange={handleInputChange}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ max: new Date().toISOString().split('T')[0] }}
                    required
                    fullWidth
                    helperText="Used to provide age-appropriate features"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2
                      }
                    }}
                  />

                  <TextField
                    name="childPIN"
                    label="Create a 4-Digit PIN"
                    type="password"
                    value={formData.childPIN}
                    onChange={handleInputChange}
                    required
                    fullWidth
                    placeholder="1234"
                    helperText="Remember this PIN - you'll need it to log in!"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Lock color="secondary" />
                        </InputAdornment>
                      ),
                    }}
                    inputProps={{
                      maxLength: 4,
                      pattern: '[0-9]*',
                      inputMode: 'numeric'
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2
                      }
                    }}
                  />

                  <TextField
                    name="childConfirmPIN"
                    label="Confirm PIN"
                    type="password"
                    value={formData.childConfirmPIN}
                    onChange={handleInputChange}
                    required
                    fullWidth
                    placeholder="1234"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Lock color="secondary" />
                        </InputAdornment>
                      ),
                    }}
                    inputProps={{
                      maxLength: 4,
                      pattern: '[0-9]*',
                      inputMode: 'numeric'
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2
                      }
                    }}
                  />

                  <Button
                    onClick={handleChildRegister}
                    variant="contained"
                    size="large"
                    disabled={loading}
                    sx={{
                      py: 1.5,
                      fontSize: '1.1rem',
                      fontWeight: 600,
                      borderRadius: 2,
                      background: 'linear-gradient(45deg, #FF9800, #FFB74D)',
                      '&:hover': {
                        background: 'linear-gradient(45deg, #F57C00, #FF9800)',
                      }
                    }}
                  >
                    {loading ? <CircularProgress size={24} color="inherit" /> : 'Create Profile 🎉'}
                  </Button>

                  <Button
                    onClick={() => {
                      setChildAuthStep(availableChildren.length > 0 ? 'select' : 'code');
                    }}
                    sx={{ textTransform: 'none' }}
                    disabled={loading}
                  >
                    ← Back
                  </Button>
                </>
              )}

              {/* Step 4: Enter PIN for Existing Child */}
              {childAuthStep === 'pin' && selectedChild && (
                <>
                  <Box sx={{ textAlign: 'center', mb: 2 }}>
                    <Box
                      component="img"
                      src={selectedChild.avatar}
                      alt={selectedChild.firstName}
                      sx={{ width: 80, height: 80, borderRadius: '50%', mb: 2 }}
                    />
                    <Typography variant="h5" gutterBottom>
                      Welcome back, {selectedChild.firstName}! 👋
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Enter your 4-digit PIN to continue
                    </Typography>
                  </Box>

                  <TextField
                    name="childPIN"
                    label="Enter Your PIN"
                    type="password"
                    value={formData.childPIN}
                    onChange={handleInputChange}
                    required
                    fullWidth
                    autoFocus
                    placeholder="••••"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Lock color="secondary" />
                        </InputAdornment>
                      ),
                    }}
                    inputProps={{
                      maxLength: 4,
                      pattern: '[0-9]*',
                      inputMode: 'numeric'
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2
                      }
                    }}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && formData.childPIN.length === 4) {
                        handleChildLogin();
                      }
                    }}
                  />

                  <Button
                    onClick={handleChildLogin}
                    variant="contained"
                    size="large"
                    disabled={loading || formData.childPIN.length !== 4}
                    sx={{
                      py: 1.5,
                      fontSize: '1.1rem',
                      fontWeight: 600,
                      borderRadius: 2,
                      background: 'linear-gradient(45deg, #FF9800, #FFB74D)',
                      '&:hover': {
                        background: 'linear-gradient(45deg, #F57C00, #FF9800)',
                      }
                    }}
                  >
                    {loading ? <CircularProgress size={24} color="inherit" /> : 'Log In'}
                  </Button>

                  <Button
                    onClick={() => setChildAuthStep('select')}
                    sx={{ textTransform: 'none' }}
                    disabled={loading}
                  >
                    ← Not {selectedChild.firstName}?
                  </Button>
                </>
              )}
            </Box>
          </TabPanel>

          {/* Footer */}
          <Box sx={{ p: 3, textAlign: 'center', bgcolor: 'grey.50' }}>
            <Typography variant="body2" color="text.secondary">
              🤖 Powered by Earn-E, your AI chore assistant
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
}

export default AuthPage;
